import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import type { ContactCondensedFormSections } from '~/components/Contacts/ContactCondensedForm';

import type { ColumnDefinition } from '~/types/components';
import type {
  Call,
  CallStatus,
  CallCampaign,
  Contact,
  Group,
  PointageResult,
  PointageStats,
  PointageStatsQuery,
  SearchResult,
  Tag,
} from '~/types/models';
import type { ErrorResponse } from '~/types/responses';
import type { ISearchSlice } from '~/types/states';
import type { StringKeys } from '~/types/utils';

export interface CallResponse {
  callAfter: string | null;
  callStatus: CallStatus;
  cellPhoneNumberInvalid: boolean;
  contact: Partial<Contact> | null;
  comment: string;
  doNotCallBack: boolean;
  hasVoted: boolean;
  homePhoneNumberInvalid: boolean;
  leftMessage: boolean;
  result: PointageResult;
  tags: Tag[];
  workPhoneNumberInvalid: boolean;
}

export interface TakeCallPayload {
  type: 'CallCampaign' | 'Group';
  id: string;
  contactId: string;
}

export const CallResponseColumns: Array<ColumnDefinition<CallResponse>> = [
  {
    key: 'callStatus',
    title: 'Statut',
    type: 'string',
  },
  {
    key: 'cellPhoneNumberInvalid',
    title: 'Cellulaire valide',
    type: 'boolean',
  },
  {
    key: 'homePhoneNumberInvalid',
    title: 'Téléphone (maison) valide',
    type: 'boolean',
  },
  {
    key: 'workPhoneNumberInvalid',
    title: 'Téléphone (travail) valide',
    type: 'boolean',
  },
  {
    key: 'comment',
    title: 'Commentaires',
    type: 'string',
  },
  {
    key: 'hasVoted',
    title: 'A voté',
    type: 'boolean',
  },
  {
    key: 'leftMessage',
    title: 'Laissé un message',
    type: 'boolean',
  },
  {
    key: 'result',
    title: 'Pointage',
    type: 'string',
  },
];

export interface IPointageSlice extends ISearchSlice {
  activeKey: string;
  call: Call | null;
  callLoaded: boolean;
  callResponse: CallResponse;
  callReturn: boolean;
  campaigns: CallCampaign[];
  columns: ContactCondensedFormSections[];
  error: ErrorResponse | null;
  groups: Group[];
  loaded: boolean;
  loading: boolean;
  searchData: SearchResult[];
  searchLoading: boolean;
  searchQuery: string | null;
  searchTotal: number | null;
  skippedContactIds: string[];
  stats: PointageStats | null;
  statsFilters: Partial<Record<StringKeys<PointageStatsQuery>, string>>;
  tags: Tag[];
}

const initialState: IPointageSlice = {
  activeKey: 'context',
  call: null,
  callLoaded: false,
  callResponse: {
    callAfter: null,
    callStatus: 'Success',
    cellPhoneNumberInvalid: false,
    contact: null,
    comment: '',
    doNotCallBack: false,
    hasVoted: false,
    homePhoneNumberInvalid: false,
    leftMessage: false,
    result: 'Unknown',
    tags: [],
    workPhoneNumberInvalid: false,
  },
  callReturn: false,
  campaigns: [],
  columns: ['basic', 'phones', 'work'],
  error: null,
  groups: [],
  loaded: false,
  loading: false,
  searchData: [],
  searchLoading: false,
  searchQuery: null,
  searchTotal: null,
  skippedContactIds: [],
  stats: null,
  statsFilters: {},
  tags: [],
};

function nextCall(_state: any, _action: PayloadAction<CallCampaign['id']>): void;
function nextCall(_state: any, _action: PayloadAction<Group['id']>): void;
function nextCall(_state: any, _action: PayloadAction<CallCampaign['id'] | Group['id']>): void {}

export const pointageSlice = createSlice({
  name: 'pointage',
  initialState,
  reducers: {
    addSkippedContactId(state, { payload: contactId }: PayloadAction<Contact['id']>) {
      if (!state.skippedContactIds.includes(contactId)) {
        state.skippedContactIds.push(contactId);
      }
    },
    freeCall(_state, _action: PayloadAction<Call['id']>) {},
    fetchCallTags(_state, _action: PayloadAction<Array<Group['id']>>) {},
    fetchPointageInit(_state) {},
    fetchStats(_state): void {},
    nextCall,
    reset(state) {
      state.skippedContactIds.splice(0, state.skippedContactIds.length);
      state.callLoaded = false;
    },
    resetCallResponse(state) {
      Object.assign(state.callResponse, {
        callAfter: null,
        callStatus: 'Success',
        cellPhoneNumberInvalid: false,
        comment: '',
        doNotCallBack: false,
        hasVoted: false,
        homePhoneNumberInvalid: false,
        leftMessage: false,
        result: 'Unknown',
        tags: [],
        workPhoneNumberInvalid: false,
      });
    },
    saveCallResponse(
      _state,
      _payload: PayloadAction<{
        contextId: string;
        id: string;
        response: CallResponse;
      }>,
    ) {},
    search: (_state, _action: PayloadAction<string>) => {},
    setActiveKey(state, { payload }) {
      state.activeKey = payload;
    },
    setCall(state, { payload }: PayloadAction<Call | null>) {
      state.call = payload;
    },
    setCallLoaded(state, { payload }: PayloadAction<boolean>) {
      state.callLoaded = payload;
    },
    setCallResponse(state, { payload }: PayloadAction<CallResponse>) {
      Object.assign(state.callResponse, payload);
    },
    setCallReturn(state, { payload }: PayloadAction<boolean>) {
      state.callReturn = payload;
    },
    setCallCampaigns(state, { payload }: PayloadAction<CallCampaign[]>) {
      const sorted = Array.from(payload).sort((a, b) => {
        if (a.stats?.remaining === a.stats?.pending || a.stats?.remaining === 0) {
          return 1;
        }
        if (b.stats?.remaining === b.stats?.pending || b.stats?.remaining === 0) {
          return -1;
        }

        return (a.stats?.remaining ?? 0) < (b.stats?.remaining ?? 0) ? -1 : 1;
      });
      state.campaigns.splice(0, state.campaigns.length, ...sorted);
    },
    setColumns(state, { payload }: PayloadAction<ContactCondensedFormSections[]>) {
      state.columns.splice(0, state.columns.length, ...payload);

      if (state.columns.length === 0) {
        state.columns.splice(0, state.columns.length, ...initialState.columns);
      }
    },
    setError(state, { payload }) {
      state.error = payload;
    },
    setGroups(state, { payload }: PayloadAction<Group[]>) {
      state.groups = payload;
    },
    setLoaded(state, { payload }: PayloadAction<boolean>) {
      state.loaded = payload;
    },
    setLoading(state, { payload }: PayloadAction<boolean>) {
      state.loading = payload;
    },
    setSearchData: (state, { payload }) => {
      state.searchData = payload;
    },
    setSearchLoading: (state, { payload }) => {
      state.searchLoading = payload;
    },
    setSearchQuery: (state, { payload }) => {
      state.searchQuery = payload;
    },
    setSearchTotal: (state, { payload }) => {
      state.searchTotal = payload;
    },
    setStats(state, { payload }: PayloadAction<PointageStats | null>) {
      state.stats = payload;
    },
    setStatsFilters(state, { payload }: PayloadAction<Partial<Record<StringKeys<PointageStatsQuery>, string>>>) {
      state.stats = null;
      state.statsFilters = payload;
    },
    setTags(state, { payload }: PayloadAction<Tag[]>) {
      state.tags = payload;
    },
    skipCall(_state, _action: PayloadAction<Call>) {},
    takeCall(_state, _action: PayloadAction<TakeCallPayload>) {},
    verifyCall(_state, _payload: PayloadAction<Call['humanId']>) {},
  },
});

const { actions, reducer: pointageReducer } = pointageSlice;

const reducer = persistReducer(
  {
    key: 'CSN_Pointage:pointage',
    storage,
    whitelist: ['callResponse', 'activeKey', 'skippedContactIds'],
  },
  pointageReducer,
);

export { actions, reducer };
